import React, { useState, useEffect } from "react";
import {
    Container,
    Row,
    Col,
    Card,
    CardBody,
    Form,
    Label,
    Input,
    Button,
    Table,
    Nav,
    NavItem,
    NavLink,
    TabContent,
    TabPane,
    Badge,
} from "reactstrap";
import classnames from "classnames";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { 
    getAccountMasters, 
    getAccountHeads, 
    getAccountSubHeads,
    getAshMappings,
    getAshMappingMasters,
    saveAshMapping,
    deleteAshMapping
} from "../../components/Api/GmsFinance";
import Swal from "sweetalert2";
import { ClipLoader } from "react-spinners";
import {
    hasPagePermissionWithAdmin,
    hasDeletePermissionWithAdmin,
    hasEditOrHigherPermission,
} from "../../components/Permission/PermissionService";

const initialForm = {
    accountMasterID: "",
    accountHeadID: "",
    accountSubHeadID: "",
    ashMappingMasterID: "",
    ashMappingCode: ""
};

const AshMappingPage = () => {
    const [activeTab, setActiveTab] = useState("1");
    const [mappings, setMappings] = useState([]);
    const [masters, setMasters] = useState([]);
    const [heads, setHeads] = useState([]);
    const [subHeads, setSubHeads] = useState([]);
    const [mappingMasters, setMappingMasters] = useState([]);
    
    const [formData, setFormData] = useState(initialForm);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    // Permissions
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const pageRoute = '/GmsAshMapping';
    const hasView = hasPagePermissionWithAdmin(permissions, pageRoute);
    const hasEdit = hasEditOrHigherPermission(permissions, pageRoute);
    const hasDelete = hasDeletePermissionWithAdmin(permissions, pageRoute);

    useEffect(() => {
        if (!hasView) {
            window.location.href = "/Dashboard";
        } else {
            loadInitialData();
            loadMappings();
        }
    }, [hasView]);

    const loadInitialData = async () => {
        try {
            const [mats, maps] = await Promise.all([
                getAccountMasters(),
                getAshMappingMasters()
            ]);
            setMasters(mats);
            setMappingMasters(maps);
        } catch (error) {
            console.error("Error loading masters:", error);
        }
    };

    const loadMappings = async () => {
        setLoading(true);
        try {
            const data = await getAshMappings();
            setMappings(data);
        } catch (error) {
            Swal.fire("Error", "Failed to load mappings", "error");
        } finally {
            setLoading(false);
        }
    };

    // Cascading Dropdowns
    useEffect(() => {
        if (formData.accountMasterID) {
            getAccountHeads(formData.accountMasterID).then(setHeads);
            // Only clear children if we are changing to a NEW master via user selection
            // If it's part of an edit (formData has accountHeadID already), don't clear.
        }
    }, [formData.accountMasterID]);

    const handleMasterChange = (e) => {
        const val = e.target.value;
        setFormData(prev => ({ 
            ...prev, 
            accountMasterID: val, 
            accountHeadID: "", 
            accountSubHeadID: "" 
        }));
        setHeads([]);
        setSubHeads([]);
    };

    const handleHeadChange = (e) => {
        const val = e.target.value;
        setFormData(prev => ({ 
            ...prev, 
            accountHeadID: val, 
            accountSubHeadID: "" 
        }));
        setSubHeads([]);
    };

    useEffect(() => {
        if (formData.accountHeadID && subHeads.length === 0) {
            getAccountSubHeads(formData.accountHeadID).then(setSubHeads);
        }
    }, [formData.accountHeadID]);

    // Handle Mapping Selection to show Read-only Code
    const handleMappingMasterChange = (e) => {
        const masterId = e.target.value;
        const selected = mappingMasters.find(m => m.ashMappingMasterID == masterId);
        setFormData(prev => ({
            ...prev,
            ashMappingMasterID: masterId,
            ashMappingCode: selected ? selected.ashMappingCode : ""
        }));
    };

    const handleEdit = (m) => {
        setFormData({
            accountMasterID: m.accountMasterID || "",
            accountHeadID: m.accountHeadID || "",
            accountSubHeadID: m.accountSubHeadID,
            ashMappingMasterID: mappingMasters.find(mm => mm.ashMappingCode === m.ashMappingCode)?.ashMappingMasterID || "",
            ashMappingCode: m.ashMappingCode
        });
        setActiveTab("1");
    };

    const handleDelete = async (m) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Remove mapping for ${m.ashMappingNameEn}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                const response = await deleteAshMapping(m.accountSubHeadID, m.ashMappingCode);
                if (response.success) {
                    Swal.fire('Deleted!', response.msg, 'success');
                    loadMappings();
                } else {
                    Swal.fire('Error', response.msg, 'error');
                }
            } catch (error) {
                Swal.fire('Error', 'Server Error', 'error');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.accountSubHeadID || !formData.ashMappingCode) {
            Swal.fire("Warning", "Please select Ledger and Mapping Name", "warning");
            return;
        }

        setSaving(true);
        try {
            const response = await saveAshMapping(formData);
            if (response.success) {
                Swal.fire("Success", response.msg, "success");
                setFormData(initialForm);
                loadMappings();
                // User requested NOT to redirect
            } else {
                Swal.fire("Error", response.msg, "error");
            }
        } catch (error) {
            Swal.fire("Error", "Server error", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="page-content">
            <Container fluid>
                <Breadcrumbs title="Finance & Accounts" breadcrumbItem="ASH Mapping" />

                <Row>
                    <Col lg={12}>
                        <Card className="shadow-sm border-0" style={{ borderRadius: '15px' }}>
                            <CardBody>
                                <div className="wizard clearfix">
                                    <div className="steps clearfix">
                                        <ul>
                                            {hasEdit && (
                                                <NavItem className={classnames({ current: activeTab === "1" })}>
                                                    <NavLink
                                                        className={classnames({ active: activeTab === "1" })}
                                                        onClick={() => {
                                                            setFormData(initialForm);
                                                            setActiveTab("1");
                                                        }}
                                                    >
                                                        <span className="number">1.</span> {formData.ashMappingCode ? "Update Mapping" : "Create Mapping"}
                                                    </NavLink>
                                                </NavItem>
                                            )}
                                            <NavItem className={classnames({ current: activeTab === "2" })}>
                                                <NavLink
                                                    className={classnames({ active: activeTab === "2" })}
                                                    onClick={() => setActiveTab("2")}
                                                >
                                                    <span className="number">2.</span> Mapping List
                                                </NavLink>
                                            </NavItem>
                                        </ul>
                                    </div>

                                    <div className="content clearfix mt-4">
                                        <TabContent activeTab={activeTab}>
                                            <TabPane tabId="1">
                                                {hasEdit ? (
                                                    <div className="p-3">
                                                        <h5 className="mb-4 text-primary">Link Account to Mapping Master</h5>
                                                        <Form onSubmit={handleSubmit}>
                                                            <Row>
                                                                <Col md={6}>
                                                                    <div className="mb-3">
                                                                        <Label className="fw-bold">Account Master <span className="text-danger">*</span></Label>
                                                                        <select className="form-select" value={formData.accountMasterID} onChange={handleMasterChange} required>
                                                                            <option value="">Select Master</option>
                                                                            {masters.map(m => <option key={m.accountMasterID} value={m.accountMasterID}>{m.accountMasterNameEn}</option>)}
                                                                        </select>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="mb-3">
                                                                        <Label className="fw-bold">Account Head <span className="text-danger">*</span></Label>
                                                                        <select className="form-select" value={formData.accountHeadID} onChange={handleHeadChange} required disabled={!formData.accountMasterID}>
                                                                            <option value="">Select Head</option>
                                                                            {heads.map(h => <option key={h.accountHeadID} value={h.accountHeadID}>{h.accountHeadCode ? `${h.accountHeadCode} - ` : ""}{h.accountHeadNameEn}</option>)}
                                                                        </select>
                                                                    </div>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <div className="mb-3">
                                                                        <Label className="fw-bold">Account Sub-Head <span className="text-danger">*</span></Label>
                                                                        <select className="form-select" value={formData.accountSubHeadID} onChange={(e) => setFormData({ ...formData, accountSubHeadID: e.target.value })} required disabled={!formData.accountHeadID}>
                                                                            <option value="">Select Account Sub-Head</option>
                                                                            {subHeads.map(s => <option key={s.accountSubHeadID} value={s.accountSubHeadID}>{s.accountSubHeadCode ? `${s.accountSubHeadCode} - ` : ""}{s.accountSubHeadNameEn}</option>)}
                                                                        </select>
                                                                    </div>
                                                                </Col>
                                                                <Col md={3}>
                                                                    <div className="mb-3">
                                                                        <Label className="fw-bold">ASH Mapping Name <span className="text-danger">*</span></Label>
                                                                        <select 
                                                                            className="form-select"
                                                                            value={formData.ashMappingMasterID} 
                                                                            onChange={handleMappingMasterChange} 
                                                                            required
                                                                            style={formData.accountSubHeadID && mappings.some(m => m.accountSubHeadID === formData.accountSubHeadID && m.ashMappingCode === formData.ashMappingCode) ? { backgroundColor: "#eff2f7", cursor: "not-allowed" } : {}}
                                                                            disabled={formData.accountSubHeadID && mappings.some(m => m.accountSubHeadID === formData.accountSubHeadID && m.ashMappingCode === formData.ashMappingCode)}
                                                                        >
                                                                            <option value="">Select Mapping</option>
                                                                            {mappingMasters.map(m => <option key={m.ashMappingMasterID} value={m.ashMappingMasterID}>{m.ashMappingNameEn}</option>)}
                                                                        </select>
                                                                    </div>
                                                                </Col>
                                                                <Col md={3}>
                                                                    <div className="mb-3">
                                                                        <Label className="fw-bold">ASH Mapping Code</Label>
                                                                        <Input type="text" className="bg-light" value={formData.ashMappingCode} readOnly placeholder="Auto-fetched code" />
                                                                    </div>
                                                                </Col>
                                                            </Row>

                                                            <div className="mt-4 d-flex gap-2">
                                                                <Button color="primary" type="submit" disabled={saving}>
                                                                    {saving && <ClipLoader size={15} color="#fff" className="me-1" />}
                                                                    {formData.accountSubHeadID && mappings.some(m => m.accountSubHeadID === formData.accountSubHeadID && m.ashMappingCode === formData.ashMappingCode) ? "Update Mapping" : "Save Mapping"}
                                                                </Button>
                                                                <Button color="light" type="button" onClick={() => { setFormData(initialForm); }}>Clear</Button>
                                                            </div>
                                                        </Form>
                                                    </div>
                                                ) : <div className="alert alert-warning">No permissions to edit.</div>}
                                            </TabPane>

                                            <TabPane tabId="2">
                                                <div className="p-2">
                                                    <h5 className="mb-4 text-primary">Active Ledgers Mappings</h5>
                                                    {loading ? (
                                                        <div className="text-center py-5">
                                                            <ClipLoader color="#556ee6" size={50} />
                                                        </div>
                                                    ) : (
                                                        <div className="table-responsive">
                                                            <Table className="table table-bordered table-hover align-middle">
                                                                <thead className="table-light">
                                                                    <tr>
                                                                        <th>Code</th>
                                                                        <th>Mapping Name</th>
                                                                        <th>Arabic Name</th>
                                                                        <th>Mapped Sub-Head</th>
                                                                        <th>Head</th>
                                                                        <th>Master</th>
                                                                        {hasDelete && <th className="text-center">Action</th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {mappings.length > 0 ? (
                                                                        mappings.map((m, idx) => (
                                                                            <tr key={idx}>
                                                                                <td><Badge color="soft-info">{m.ashMappingCode}</Badge></td>
                                                                                <td className="fw-bold">{m.ashMappingNameEn}</td>
                                                                                <td>{m.ashMappingNameAr || "-"}</td>
                                                                                <td><span className="text-primary fw-bold">({m.accountSubHeadCode})</span> {m.accountSubHeadNameEn}</td>
                                                                                <td>{m.accountHeadNameEn}</td>
                                                                                <td>{m.accountMasterNameEn}</td>
                                                                                {hasEdit && (
                                                                                    <td className="text-center">
                                                                                        <Button size="sm" color="soft-primary" className="me-2" onClick={() => handleEdit(m)}>
                                                                                            <i className="bx bxs-edit font-size-16"></i> Edit
                                                                                        </Button>
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        ))
                                                                    ) : <tr><td colSpan="7" className="text-center py-3">No mappings found</td></tr>}
                                                                </tbody>
                                                            </Table>
                                                        </div>
                                                    )}
                                                </div>
                                            </TabPane>
                                        </TabContent>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AshMappingPage;
